import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { client, idempotent, newIdempotencyKey, unwrap } from "./client";
import { withStepUp } from "./mfa";
import type {
  AcceptSuggestionsResult,
  Accepted,
  AccountMapping,
  AcceptInvitationBody,
  AcceptInvitationResult,
  AuditEvent,
  AuthorityGap,
  BatchCompleteness,
  BatchRunCreated,
  BatchRunDetail,
  BatchRunSummary,
  CapabilityMatrix,
  ChangeRoleBody,
  ClientMigrationState,
  Connection,
  ConversionBasisSuggestion,
  PreflightOverrideBody,
  ConnectorSwitch,
  Consent,
  ContactMerge,
  CreateBatchRunBody,
  CreateInvitationBody,
  CreateMigrationBody,
  CreatePracticeBody,
  Entity,
  ControlTotal,
  EvidencePack,
  FxOpeningBalances,
  HmrcVerification,
  Invitation,
  Job,
  Member,
  Migration,
  MigrationEvent,
  MigrationProfile,
  MigrationState,
  Page,
  PackageManifest,
  PracticeClient,
  PracticeDetail,
  PracticeSummary,
  PurgeCount,
  RateBudget,
  ReviewItem,
  Scope,
  Scorecard,
  SignoffBody,
  SignoffVatLock,
  Systems,
  TaxMapping,
  VendorAlert,
  VendorLimit,
  XeroAuthorization,
  XeroHandoff,
} from "./types";
import { TERMINAL_STATES, WAITING_STATES } from "./types";

/**
 * One hook per operation (§B1.2), with the query-key convention `["migration", id, <sub>]` and an explicit
 * invalidation after every mutation. Polling intervals (§A9.8) live here and never in a component.
 */

export const keys = {
  session: ["session"] as const,
  systems: ["systems"] as const,
  systemCapabilities: (system: string) => ["systems", system, "capabilities"] as const,
  connections: ["connections"] as const,
  handoff: (handoffId: string) => ["handoff", handoffId] as const,
  migrations: (filter: string, cursor: string | null) => ["migrations", filter, cursor] as const,
  migration: (id: string) => ["migration", id] as const,
  sub: (id: string, sub: string, extra?: string) => ["migration", id, sub, extra ?? ""] as const,
  job: (jobId: string) => ["job", jobId] as const,
  admin: (what: string) => ["admin", what] as const,
  practices: ["practices"] as const,
  practice: (practiceId: string) => ["practice", practiceId] as const,
  practiceMembers: (practiceId: string) => ["practice", practiceId, "members"] as const,
  practiceInvitations: (practiceId: string) => ["practice", practiceId, "invitations"] as const,
  practiceClients: (practiceId: string, cursor: string | null) =>
    ["practice", practiceId, "clients", cursor] as const,
  practiceBatchRuns: (practiceId: string, cursor: string | null) =>
    ["practice", practiceId, "batch-runs", cursor] as const,
  authorityGaps: (practiceId: string) => ["practice", practiceId, "authority-gaps"] as const,
  rateBudget: (practiceId: string) => ["practice", practiceId, "rate-budget"] as const,
  batchRun: (batchRunId: string) => ["batch-run", batchRunId] as const,
  batchRunClients: (batchRunId: string, cursor: string | null) =>
    ["batch-run", batchRunId, "clients", cursor] as const,
  batchRunCompleteness: (batchRunId: string) => ["batch-run", batchRunId, "completeness"] as const,
};

/** §A9.8: 2 s while the migration is doing work, 30 s while it waits for a human, off once terminal. */
export function pollInterval(state: MigrationState | undefined): number | false {
  if (state === undefined) return 2000;
  if (TERMINAL_STATES.has(state)) return false;
  return WAITING_STATES.has(state) ? 30_000 : 2000;
}

/**
 * After the operator's own action (sign-off, run, retry, resolve, accept ...) the answer usually says the job is
 * only QUEUED: the row still reads its old state, and if that is a waiting state `pollInterval` would wait 30 s
 * before looking again -- the page seemed dead until a reload. For up to two minutes after an action, or until
 * the state moves, the migration is polled at the working cadence instead. Module state (not React state) so
 * every hook of every mounted screen sees the same watch, and it is read at call time by each poll.
 */
const EXPECT_TRANSITION_MS = 120_000;
const expectingTransition = new Map<string, { from: MigrationState | undefined; until: number }>();

/** `pollInterval`, except while a transition the operator just asked for has not shown up yet. */
export function pollIntervalFor(migrationId: string, state: MigrationState | undefined): number | false {
  const watch = expectingTransition.get(migrationId);
  if (watch !== undefined) {
    if (state !== watch.from || Date.now() > watch.until) {
      expectingTransition.delete(migrationId);
    } else if (!TERMINAL_STATES.has(state as MigrationState)) {
      return 2000;
    }
  }
  return pollInterval(state);
}

/** Every user action ends here: refetch what the migration shows, and (`mayMoveState`) watch for the transition. */
function refreshMigration(qc: QueryClient, migrationId: string, mayMoveState = false): void {
  if (mayMoveState) {
    const cached = qc.getQueryData<Migration>(keys.migration(migrationId));
    expectingTransition.set(migrationId, {
      from: cached?.state as MigrationState | undefined,
      until: Date.now() + EXPECT_TRANSITION_MS,
    });
  }
  void qc.invalidateQueries({ queryKey: keys.migration(migrationId) });
}

/**
 * The sign-off pending watch. A sign-off answers 202 while the worker still runs, and the row keeps its old state
 * for seconds: a button that re-enabled then invited a second click. From the moment a sign-off is accepted until
 * the migration state has moved (or `EXPECT_TRANSITION_MS` has passed, `stalled`) the sign-off controls of every
 * screen read `waiting`. Separate from `expectingTransition` above, which a plain resolve also arms and must not
 * lock the sign-off button. External store so a screen that did not start the sign-off (the dialog's parent) renders it.
 */
interface SignoffWatch {
  from: MigrationState | undefined;
  until: number;
  stalled: boolean;
}
const signoffWatches = new Map<string, SignoffWatch>();
const signoffListeners = new Set<() => void>();
let signoffVersion = 0;

function notifySignoffWatch(): void {
  signoffVersion += 1;
  signoffListeners.forEach((listener) => listener());
}

/** Arm the watch for a sign-off the server just accepted; `from` is the state it was accepted in. */
export function beginSignoffWatch(migrationId: string, from: MigrationState | string | undefined): void {
  signoffWatches.set(migrationId, {
    from: from as MigrationState | undefined,
    until: Date.now() + EXPECT_TRANSITION_MS,
    stalled: false,
  });
  notifySignoffWatch();
}

/** Test isolation: forget every watch. */
export function resetSignoffWatch(): void {
  signoffWatches.clear();
  notifySignoffWatch();
}

/**
 * `waiting`: a sign-off was accepted and the state has not moved yet -- keep the controls disabled and labelled
 * "Working". `stalled`: it did not move within the window -- controls are usable again, with a hint to refresh.
 */
export function useSignoffWatch(
  migrationId: string,
  state: MigrationState | string | undefined,
): { waiting: boolean; stalled: boolean } {
  useSyncExternalStore(
    (listener) => {
      signoffListeners.add(listener);
      return () => void signoffListeners.delete(listener);
    },
    () => signoffVersion,
  );
  const watch = signoffWatches.get(migrationId);
  const moved = watch !== undefined && state !== undefined && state !== watch.from;
  const active = watch !== undefined && !moved;
  useEffect(() => {
    if (watch === undefined) return;
    if (moved) {
      signoffWatches.delete(migrationId);
      notifySignoffWatch();
      return;
    }
    if (watch.stalled) return;
    const timer = setTimeout(
      () => {
        const current = signoffWatches.get(migrationId);
        if (current === watch) {
          current.stalled = true;
          notifySignoffWatch();
        }
      },
      Math.max(0, watch.until - Date.now()) + 1,
    );
    return () => clearTimeout(timer);
  }, [watch, moved, migrationId]);
  const stalled = active && watch.stalled;
  return { waiting: active && !stalled, stalled };
}

// ---------------------------------------------------------------- meta and connections

export function useSystems(): UseQueryResult<Systems> {
  return useQuery({
    queryKey: keys.systems,
    queryFn: () => unwrap(client.GET("/api/v1/systems", {})) as Promise<Systems>,
    staleTime: 60_000,
  });
}

export function useConnections(options?: {
  enabled?: boolean;
  /** The "Show history" view: also the rows a later reconnect of the same organisation replaced. */
  includeSuperseded?: boolean;
}): UseQueryResult<Connection[]> {
  const includeSuperseded = options?.includeSuperseded ?? false;
  return useQuery({
    // The history view is its own cache entry; `invalidateQueries({ queryKey: keys.connections })` matches both.
    queryKey: includeSuperseded ? [...keys.connections, "history"] : keys.connections,
    enabled: options?.enabled ?? true,
    // `list_connections` answers `ConnectionsOut` - `{items: [...]}` - and §A4.4 says connections are dozens,
    // so the hook unwraps the envelope and every screen sees the bare array it was promised.
    //
    // `include_revoked: true` (Opus review): the server defaults `include_revoked` to `false`
    // (`GET /connections`, `routers/connections.py`), so without this a revoked LuciBook row never
    // reaches ANY screen — including `ConnectionsScreen`, whose "Reconnect with LuciBook" button can only
    // render for a row it never receives. `ConnectScreen`'s own `pick()` already filters
    // `revoke_state === "revoked"` back out client-side (it always expected revoked rows to arrive and
    // be excluded there, not excluded by the server), so requesting them here changes no other screen's
    // behaviour.
    queryFn: async () =>
      (
        (await unwrap(
          client.GET("/api/v1/connections", {
            params: {
              query: includeSuperseded
                ? { include_revoked: true, include_superseded: true }
                : { include_revoked: true },
            },
          }),
        )) as { items: Connection[] }
      ).items,
  });
}

export function useStartXeroAuthorization() {
  return useMutation({
    mutationFn: (body: { display_name?: string; redirect_path?: string }) =>
      unwrap(
        client.POST("/api/v1/connections/xero/authorize", {
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<XeroAuthorization>,
  });
}

/**
 * Package W2c: QBO/FreeAgent/Sage BC each get their own `start_*_authorization` hook, mirroring
 * `useStartXeroAuthorization` exactly except for the absence of a client-supplied `display_name` — none of
 * the three has one (the callback derives `display_name` from the vendor's own identity: e-mail, realm id,
 * or business name), so the body is just `{redirect_path?}`, which none of the three callbacks honours
 * either (`_connections_return` always lands on `/connections`) but is accepted for parity with Xero's body.
 */
export function useStartQboAuthorization() {
  return useMutation({
    mutationFn: (body: { redirect_path?: string } = {}) =>
      unwrap(
        client.POST("/api/v1/connections/qbo/authorize", {
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<XeroAuthorization>,
  });
}

export function useStartFreeAgentAuthorization() {
  return useMutation({
    mutationFn: (body: { redirect_path?: string } = {}) =>
      unwrap(
        client.POST("/api/v1/connections/freeagent/authorize", {
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<XeroAuthorization>,
  });
}

export function useStartSageBcAuthorization() {
  return useMutation({
    mutationFn: (body: { redirect_path?: string } = {}) =>
      unwrap(
        client.POST("/api/v1/connections/sage_bc/authorize", {
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<XeroAuthorization>,
  });
}

/**
 * LuciBook's OAuth2 capture-app flow, the same shape as `useStartQboAuthorization` above: no client-supplied
 * `display_name` (the callback keeps `display_name == base_url`, §A6.6's invariant), and
 * `lucibook_oauth_callback` always lands on `/connections`.
 */
export function useStartLuciBookAuthorization() {
  return useMutation({
    mutationFn: (body: { redirect_path?: string } = {}) =>
      unwrap(
        client.POST("/api/v1/connections/lucibook/authorize", {
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<XeroAuthorization>,
  });
}

export function useXeroHandoff(handoffId: string | null): UseQueryResult<XeroHandoff> {
  return useQuery({
    queryKey: keys.handoff(handoffId ?? ""),
    enabled: handoffId !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/connections/xero/handoff/{handoff_id}", {
          params: { path: { handoff_id: handoffId as string } },
        }),
      ) as Promise<XeroHandoff>,
  });
}

export function useSelectXeroTenant(handoffId: string) {
  const qc = useQueryClient();
  const key = useRef(newIdempotencyKey());
  return useMutation({
    mutationFn: (body: { tenant_id: string; display_name?: string }) =>
      unwrap(
        client.POST("/api/v1/connections/xero/handoff/{handoff_id}/select", {
          params: { path: { handoff_id: handoffId } },
          body,
          headers: idempotent(key.current),
        }),
      ) as Promise<Connection>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.connections }),
  });
}

//  `useCreateLuciBookConnection` (the `client_credentials` "custom connection" mutation, `POST
//  /api/v1/connections/lucibook`) is REMOVED - 2026-09-24 owner decision. LuciBook is connected through
//  `useStartLuciBookAuthorization` now, the same OAuth authorize+callback handoff QBO/FreeAgent/Sage BC use.

export function useCreateFakeConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: "source" | "destination"; org_id: string; display_name: string }) =>
      unwrap(
        client.POST("/api/v1/connections/fake", { body, headers: idempotent(newIdempotencyKey()) }),
      ) as Promise<Connection>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.connections }),
  });
}

export function useRevokeConnection() {
  const qc = useQueryClient();
  return useMutation({
    // xmg-sec-mfa: a sensitive action - a stale second factor is asked for again, then the call is retried once.
    mutationFn: (connectionId: string) =>
      withStepUp(
        () =>
          unwrap(
            client.DELETE("/api/v1/connections/{connection_id}", {
              params: { path: { connection_id: connectionId } },
              headers: idempotent(newIdempotencyKey()),
            }),
          ) as Promise<Accepted>,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.connections }),
  });
}

// ---------------------------------------------------------------- migrations

export function useMigrations(filter: {
  state?: string;
  source_system?: string;
  destination_system?: string;
  cursor?: string | null;
  limit?: number;
}) {
  const state = filter.state ?? "";
  const source = filter.source_system ?? "";
  const destination = filter.destination_system ?? "";
  const cursor = filter.cursor ?? null;
  return useQuery({
    queryKey: keys.migrations(`${state}|${source}|${destination}`, cursor),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations", {
          params: {
            query: {
              ...(state === "" ? {} : { state }),
              ...(source === "" ? {} : { source_system: source }),
              ...(destination === "" ? {} : { destination_system: destination }),
              ...(cursor === null ? {} : { cursor }),
              limit: filter.limit ?? 50,
            },
          },
        }),
      ) as Promise<Page<Migration>>,
    placeholderData: (previous) => previous,
  });
}

export function useMigration(migrationId: string | null): UseQueryResult<Migration> {
  return useQuery({
    queryKey: keys.migration(migrationId ?? ""),
    enabled: migrationId !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}", {
          params: { path: { migration_id: migrationId as string } },
        }),
      ) as Promise<Migration>,
    refetchInterval: (query) => {
      // F3 (C15): a daily-cap pause can last up to 25h with nothing new to show until `retry_at` — no
      // reason to keep polling every 2s (or even every 30s for a signoff wait) through the whole window.
      if (query.state.data?.pause) return 30_000;
      return pollIntervalFor(migrationId ?? "", query.state.data?.state as MigrationState | undefined);
    },
  });
}

/**
 * §A4.5/§B6.1: keep the newest event id, ask only for what is new, append rather than replace. The accumulated
 * list lives in this hook so every screen that shows a timeline sees the same one.
 */
export function useEvents(migrationId: string | null, state: MigrationState | undefined) {
  const [events, setEvents] = useState<MigrationEvent[]>([]);
  const newest = useRef<string | null>(null);
  const query = useQuery({
    queryKey: keys.sub(migrationId ?? "", "events"),
    enabled: migrationId !== null,
    refetchInterval: () => pollIntervalFor(migrationId ?? "", state),
    queryFn: async () => {
      const since = newest.current;
      const page = (await unwrap(
        client.GET("/api/v1/migrations/{migration_id}/events", {
          params: {
            path: { migration_id: migrationId as string },
            query: { limit: 100, ...(since === null ? {} : { since_id: since }) },
          },
        }),
      )) as Page<MigrationEvent>;
      if (page.items.length > 0) {
        const ascending = since === null ? [...page.items].reverse() : page.items;
        const last = ascending[ascending.length - 1];
        if (last !== undefined) newest.current = last.id;
        setEvents((prev) => [...prev, ...ascending]);
      }
      return page;
    },
  });
  // Newest first on screen; the accumulator is kept ascending so `since_id` stays trivial.
  return { ...query, events: [...events].reverse() };
}

/**
 * xmg-webux (first staging trial, defect 5): the few newest transitions, newest first, for the compact live
 * activity strip every migration screen shows. Its OWN key (not `useEvents`'s): `useEvents` keeps a
 * `since_id` accumulator per hook instance, and two instances on one key would share one `queryFn`, leaving
 * the other's list empty. Without `since_id` the endpoint's first page is the newest events (`useEvents`
 * reverses it for the same reason).
 */
export const RECENT_EVENTS_LIMIT = 5;

export function useRecentEvents(
  migrationId: string | null,
  state: MigrationState | undefined,
): UseQueryResult<MigrationEvent[]> {
  return useQuery({
    queryKey: keys.sub(migrationId ?? "", "events-recent"),
    enabled: migrationId !== null && migrationId !== "",
    refetchInterval: () => pollIntervalFor(migrationId ?? "", state),
    queryFn: async () => {
      const page = (await unwrap(
        client.GET("/api/v1/migrations/{migration_id}/events", {
          params: { path: { migration_id: migrationId as string }, query: { limit: RECENT_EVENTS_LIMIT } },
        }),
      )) as Page<MigrationEvent>;
      return [...page.items].sort((a, b) =>
        a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
      );
    },
  });
}

export type PackageBounce = "rerunning" | "returned" | "rerunning_after_signoff" | "returned_after_signoff";

/**
 * xmg-actions-first: did the conversion gate bounce a sign-off? `bounce_conversion_gate` moves
 * `awaiting_conversion_signoff -> transforming` when an answer or an update changed the package after the dry
 * run; the transition's note is prose for people, so the transition itself (the one the events endpoint exposes
 * as `from_state`/`to_state`) is what is matched. `rerunning`: the bounce is the newest sign-off-gate event and
 * the migration is still re-running; `returned`: the dry run is back at the gate after it. A later event that
 * leaves the gate for anywhere else (a sign-off that went through) ends the story.
 *
 * XMG-LOADING-DRIFT: a package that changed AFTER the sign-off, found before anything was loaded, is the same story
 * one step later: the sign-off took `awaiting_conversion_signoff -> loading`, then the load stage sent the
 * migration `loading -> transforming` (recording `previous_package_sha256`) for a re-run and a fresh sign-off.
 */
export function packageBounceOf(
  events: readonly MigrationEvent[],
  state: MigrationState | undefined,
): PackageBounce | null {
  const gate = "awaiting_conversion_signoff";
  // `events` is newest first. The first event that LEFT the gate (or sent a signed-off, not yet loaded package
  // from `loading` back to `transforming`) decides what happened.
  const index = events.findIndex(
    (event) =>
      event.from_state === gate || (event.from_state === "loading" && event.to_state === "transforming"),
  );
  const bounce = index === -1 ? undefined : events[index];
  if (bounce === undefined || bounce.to_state !== "transforming") return null;
  // A bounce records the package it replaced; an HMRC re-run makes the same transition without it.
  const payload = (bounce as { payload?: unknown }).payload;
  if (typeof payload !== "object" || payload === null || !("previous_package_sha256" in payload)) return null;
  // Between the bounce and now only the re-run itself may have happened: transform -> dry run -> back at the
  // gate. Anything else (a failure, a rollback, a reopen) ends the story, so no stale banner is shown.
  const rerun = events.slice(0, index);
  const onlyRerun = rerun.every(
    (event) =>
      (event.from_state === "transforming" && event.to_state === "dry_running") ||
      (event.from_state === "dry_running" && event.to_state === gate),
  );
  if (!onlyRerun) return null;
  // `_after_signoff`: the user had already signed and the load stage took the package back (nothing was loaded).
  const afterSignoff = bounce.from_state === "loading";
  if (state === "transforming" || state === "dry_running") {
    return afterSignoff ? "rerunning_after_signoff" : "rerunning";
  }
  if (state === gate && rerun.length > 0 && rerun[0]?.to_state === gate) {
    return afterSignoff ? "returned_after_signoff" : "returned";
  }
  return null;
}

/**
 * xmg-webux (first staging trial, defect 5): `useMigration` polls the migration row, but every other query
 * on a migration screen (control totals, scorecard, review items, mappings, FX balances, evidence...) is a
 * one-off snapshot -- after a dry run finished or failed, or the migration reached hypercare, the operator
 * had to reload to see it. When the migration makes a meaningful transition, every query keyed under this
 * migration (`["migration", id, <sub>, ...]`) is invalidated, so mounted screens refetch what they show.
 *
 * xmg-webux2: the marker is the row's `state` plus the id of the newest event in the `/events` feed (the
 * same `useRecentEvents` query the live activity strip polls, so it costs no extra request where the strip
 * is mounted). It used to be `state|updated_at`, but `updated_at` moves on every runtime write, so while a
 * worker was busy every mounted query for the migration was refetched on every 2s poll. A transition writes
 * an event; a progress write does not.
 *
 * The first observation is not a change (nothing is stale yet), and no marker exists until both the row and
 * the events feed have answered. The row's own key is left alone (it is what is being watched). The
 * recent-events feed IS invalidated: a state change seen on the row first must pull the transition's event
 * (a terminal state stops that feed's own poll, so nothing else would). That refetch can move the marker once
 * more (the new event id), which invalidates once more and then settles: the marker only changes again on
 * the next transition.
 */
export function liveMigrationMarker(
  migration: Migration | undefined,
  recentEvents: readonly MigrationEvent[] | undefined,
): string | null {
  if (migration === undefined || recentEvents === undefined) {
    return null;
  }
  return `${migration.state}|${recentEvents[0]?.id ?? ""}`;
}

/**
 * xmg-lmux (3): a screen that already polls the full `/events` feed (Status, through `useEvents`) hands it in
 * as `eventsFeed`, newest first (`undefined` until it has answered). The marker is then read off that feed and
 * the separate `limit=5` recent-events poll is never started -- one events request per tick, not two.
 */
export interface LiveEventsFeed {
  events: readonly MigrationEvent[] | undefined;
}

export function useLiveMigrationRefresh(
  migrationId: string,
  migration: Migration | undefined,
  eventsFeed?: LiveEventsFeed,
): void {
  const qc = useQueryClient();
  const recent = useRecentEvents(
    migrationId === "" || eventsFeed !== undefined ? null : migrationId,
    migration?.state as MigrationState | undefined,
  );
  const seen = useRef<string | null>(null);
  const marker = liveMigrationMarker(migration, eventsFeed === undefined ? recent.data : eventsFeed.events);
  useEffect(() => {
    if (marker === null) {
      return;
    }
    const previous = seen.current;
    seen.current = marker;
    if (previous === null || previous === marker) {
      return;
    }
    void qc.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "migration" && query.queryKey[1] === migrationId && query.queryKey.length > 2,
    });
  }, [marker, migrationId, qc]);
}

// `JobOut.job_id` and the `/jobs/{job_id}` path parameter are both integers: procrastinate keys jobs on a
// BIGINT. The hook therefore takes a number, not a string (§A9.10).
export function useJob(jobId: number | null): UseQueryResult<Job> {
  return useQuery({
    queryKey: keys.job(String(jobId ?? "")),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "todo" || status === "doing" || status === undefined ? 2000 : false;
    },
    queryFn: () =>
      unwrap(
        // `job_id` is a procrastinate BIGINT: the contract types the path parameter as an integer.
        client.GET("/api/v1/jobs/{job_id}", { params: { path: { job_id: Number(jobId) } } }),
      ) as Promise<Job>,
  });
}

const JOB_POLL_MS = 1000;
const JOB_POLL_LIMIT = 180;
const JOB_FINISHED = new Set(["succeeded", "failed", "cancelled", "aborted"]);

/** The file's bytes via `FileReader` (every browser, and jsdom, which has no `Blob.arrayBuffer`). */
function readBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error("the file could not be read"));
    reader.readAsArrayBuffer(file);
  });
}

/** Wait for a deferred job to finish (a `null` id means the job was already queued: nothing to wait on). */
async function waitForJob(jobId: number | null | undefined): Promise<Job | null> {
  if (jobId === null || jobId === undefined) return null;
  for (let attempt = 0; attempt < JOB_POLL_LIMIT; attempt += 1) {
    const job = (await unwrap(
      client.GET("/api/v1/jobs/{job_id}", { params: { path: { job_id: jobId } } }),
    )) as Job;
    if (JOB_FINISHED.has(job.status)) return job;
    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_MS));
  }
  return null;
}

/**
 * Manual Xero Journal Report upload: the existing §D13 file path, end to end (create the session, tus-append the
 * bytes, `scan` = ClamAV + format sniff, `parse` = the Journal Report reader), then the Scope query is refreshed
 * so the screen shows the outcome. The report itself is only read by the server.
 */
export function useUploadJournalReport(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<void> => {
      const session = await unwrap(
        client.POST("/api/v1/migrations/{migration_id}/files", {
          params: { path: { migration_id: migrationId } },
          body: {
            filename: file.name,
            byte_size: file.size,
            content_type: file.type === "" ? "application/octet-stream" : file.type,
          },
          headers: idempotent(newIdempotencyKey()),
        }),
      );
      const bytes = await readBytes(file);
      let offset = 0;
      while (offset < bytes.length) {
        const chunk = bytes.slice(offset, offset + session.chunk_size);
        await unwrap(
          client.PATCH("/api/v1/files/{file_id}", {
            params: { path: { file_id: session.file_id }, header: { "Upload-Offset": offset } },
            body: chunk as unknown as string,
            bodySerializer: (body) => body as unknown as BodyInit,
            headers: { "Content-Type": "application/offset+octet-stream" },
          }),
        );
        offset += chunk.length;
      }
      const scan = await unwrap(
        client.POST("/api/v1/files/{file_id}/scan", {
          params: { path: { file_id: session.file_id } },
          headers: idempotent(newIdempotencyKey()),
        }),
      );
      await waitForJob(scan.job_id);
      const parse = await unwrap(
        client.POST("/api/v1/files/{file_id}/parse", {
          params: { path: { file_id: session.file_id } },
          headers: idempotent(newIdempotencyKey()),
        }),
      );
      await waitForJob(parse.job_id);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "scope") });
      refreshMigration(qc, migrationId);
    },
  });
}

export function useScope(migrationId: string): UseQueryResult<Scope> {
  return useQuery({
    queryKey: keys.sub(migrationId, "scope"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/scope", {
          params: { path: { migration_id: migrationId } },
        }),
      ) as Promise<Scope>,
  });
}

export function useUpdateScope(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      unwrap(
        client.PATCH("/api/v1/migrations/{migration_id}/scope", {
          params: { path: { migration_id: migrationId } },
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<Scope>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "scope") });
      refreshMigration(qc, migrationId);
    },
  });
}

/**
 * The wizard's source-derived defaults. `retry: false`: a source that cannot be read answers 200 with
 * `source_unreadable`, and a transport failure should drop the step to a plain explanation at once, not after
 * backoff. NOT cached forever: a blocked state ("lock your books") is exactly the one the person fixes in another
 * tab, so the reading is stale at once and is taken again when the window regains focus (and by "Check again").
 */
export function useConversionBasisSuggestion(
  sourceConnectionId: string | undefined,
  destinationConnectionId?: string,
): UseQueryResult<ConversionBasisSuggestion> {
  return useQuery({
    enabled: sourceConnectionId !== undefined && sourceConnectionId !== "",
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryKey: [
      "connections",
      sourceConnectionId ?? "",
      "conversion-basis-suggestion",
      destinationConnectionId ?? "",
    ],
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/connections/{connection_id}/conversion-basis-suggestion", {
          params: {
            path: { connection_id: sourceConnectionId ?? "" },
            query:
              destinationConnectionId === undefined || destinationConnectionId === ""
                ? {}
                : { destination_connection_id: destinationConnectionId },
          },
        }),
      ) as Promise<ConversionBasisSuggestion>,
  });
}

/** Records an override for a failed preflight; the caller then retries (`useRetry`). */
export function useSetPreflightOverride(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PreflightOverrideBody) =>
      unwrap(
        client.POST("/api/v1/migrations/{migration_id}/preflight-override", {
          params: { path: { migration_id: migrationId } },
          body,
        }),
      ) as Promise<Scope>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "scope") });
      refreshMigration(qc, migrationId);
    },
  });
}

export function useUpdateConversionBasis(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { conversion_date?: string; base_currency?: string }) =>
      unwrap(
        client.PATCH("/api/v1/migrations/{migration_id}/basis", {
          params: { path: { migration_id: migrationId } },
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<Migration>,
    onSuccess: () => {
      refreshMigration(qc, migrationId);
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "scope") });
    },
  });
}

export function useProfile(migrationId: string, enabled = true): UseQueryResult<MigrationProfile> {
  return useQuery({
    enabled,
    queryKey: keys.sub(migrationId, "profile"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/profile", {
          params: { path: { migration_id: migrationId } },
        }),
      ) as Promise<MigrationProfile>,
  });
}

export function useCapabilities(migrationId: string): UseQueryResult<CapabilityMatrix> {
  return useQuery({
    queryKey: keys.sub(migrationId, "capabilities"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/capabilities", {
          params: { path: { migration_id: migrationId } },
        }),
      ) as Promise<CapabilityMatrix>,
  });
}

export function useConsent(migrationId: string): UseQueryResult<Consent> {
  return useQuery({
    queryKey: keys.sub(migrationId, "consent"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/consent", {
          params: { path: { migration_id: migrationId } },
        }),
      ) as Promise<Consent>,
  });
}

/**
 * §A9.6 + v2-H-8: `create_migration` carries an `Idempotency-Key`, and the consent form mints it **once at mount**
 * (`useRef` in `ConsentForm`, task 07) and hands it in. This hook never mints one of its own, because a key minted
 * per click is the double-submit bug the rule exists to prevent.
 */
export function useCreateMigration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { idempotencyKey: string; body: CreateMigrationBody }) =>
      unwrap(
        client.POST("/api/v1/migrations", { body: args.body, headers: idempotent(args.idempotencyKey) }),
      ) as Promise<Migration>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["migrations"] }),
  });
}

// ---------------------------------------------------------------- actions (§A8 40–45)

function useAction<B extends Record<string, unknown>>(
  migrationId: string,
  path: ActionPath,
  options: { signoff?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    // xmg-sec-mfa: `withStepUp` is a no-op unless the server answers `mfa_step_up_required`, which only the
    // sign-off route does; every other action passes straight through.
    mutationFn: (body: B) =>
      withStepUp(
        () =>
          unwrap(
            // openapi-fetch resolves the literal path type; the union is narrowed by the caller.
            client.POST(path as "/api/v1/migrations/{migration_id}/actions/run", {
              params: { path: { migration_id: migrationId } },
              body: body as never,
              headers: idempotent(newIdempotencyKey()),
            }),
          ) as Promise<Accepted>,
      ),
    // The state the sign-off was clicked in, read BEFORE the request: a quick worker could move the cached row
    // on before the answer arrives, and a watch on the new state would then never see it move.
    onMutate: () => ({ from: qc.getQueryData<Migration>(keys.migration(migrationId))?.state }),
    onSuccess: (_data, _body, context) => {
      if (options.signoff === true) beginSignoffWatch(migrationId, context?.from);
      refreshMigration(qc, migrationId, true);
      void qc.invalidateQueries({ queryKey: ["migrations"] });
    },
  });
}

type ActionPath =
  | "/api/v1/migrations/{migration_id}/actions/advance"
  | "/api/v1/migrations/{migration_id}/actions/run"
  | "/api/v1/migrations/{migration_id}/actions/signoff"
  | "/api/v1/migrations/{migration_id}/actions/retry"
  | "/api/v1/migrations/{migration_id}/actions/cancel"
  | "/api/v1/migrations/{migration_id}/actions/delta"
  | "/api/v1/migrations/{migration_id}/actions/rerun-hmrc"
  | "/api/v1/migrations/{migration_id}/actions/remap";

export const useAdvance = (id: string) => useAction(id, "/api/v1/migrations/{migration_id}/actions/advance");
export const useRun = (id: string) => useAction(id, "/api/v1/migrations/{migration_id}/actions/run");
export const useRetry = (id: string) => useAction(id, "/api/v1/migrations/{migration_id}/actions/retry");
export const useCancel = (id: string) =>
  useAction<{ reason: string }>(id, "/api/v1/migrations/{migration_id}/actions/cancel");
/** xmg-coafast2: re-run the mapping of a migration under review (the review screen's "Re-run mapping"). */
export const useRemap = (id: string) => useAction(id, "/api/v1/migrations/{migration_id}/actions/remap");
export const useRunDelta = (id: string) => useAction(id, "/api/v1/migrations/{migration_id}/actions/delta");

/**
 * Phase 3 spec §9F. `retry_attachments` is synchronous and DB-only (§A9.9-style exemption, like `cancel`)
 * and answers `AttachmentsRetryOut` (`{reset, phase}`), never `Accepted` -- it cannot go through the
 * generic `useAction` above, which types every response as a job acceptance. No `Idempotency-Key`: the
 * route's own docstring says a repeat call is idempotent in the data (a second call with nothing left at
 * `FAILED_UPLOAD` resets zero rows), the same reasoning `useCancel`'s sibling actions already rely on.
 */
export function useRetryAttachments(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        client.POST("/api/v1/migrations/{migration_id}/actions/retry-attachments", {
          params: { path: { migration_id: migrationId } },
        }),
      ) as Promise<{ reset: number; phase: string; fetch_retry_blocked: boolean }>,
    onSuccess: () => refreshMigration(qc, migrationId),
  });
}

/**
 * §B3.4/§B4.6/§B5.4: sign-off is **one** call and there is never a second one after it — `runner.signoff` chains
 * into `advance_migration` itself (§A9.5). `expected_package_sha256` is only sent from the conversion sign-off.
 */
export const useSignoff = (id: string) =>
  // `SignoffBody` IS `components["schemas"]["SignoffIn"]` (`api/types.ts`), which FORBIDS extra keys (§A9.3).
  // It is never retyped by hand here: a hand-written shape is what let task 08 post a `signer` and get a 422
  // on every conversion sign-off, with nothing in `npm run typecheck` to catch it.
  useAction<SignoffBody>(id, "/api/v1/migrations/{migration_id}/actions/signoff", { signoff: true });

/**
 * xmg-lmux (1): the VAT-lock consequence the conversion sign-off dialog shows -- the months LuciBook's own dry-run
 * plan will lock (or that none will be, and whether HMRC is attached) and the open VAT_LOCK items the sign-off
 * acknowledges. Keyed under the migration, so `useLiveMigrationRefresh` refetches it on a transition.
 */
export function useSignoffVatLock(migrationId: string, enabled = true): UseQueryResult<SignoffVatLock> {
  return useQuery({
    queryKey: keys.sub(migrationId, "signoff-vat-lock"),
    enabled: enabled && migrationId !== "",
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/signoff/vat-lock", {
          params: { path: { migration_id: migrationId } },
        }),
      ) as Promise<SignoffVatLock>,
  });
}

// ---------------------------------------------------------------- hmrc-rerun-action design §5.4/§3.6

export function useHmrcVerification(migrationId: string | null): UseQueryResult<HmrcVerification> {
  return useQuery({
    queryKey: keys.sub(migrationId ?? "", "hmrc-verification"),
    enabled: migrationId !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/hmrc-verification", {
          params: { path: { migration_id: migrationId as string } },
        }),
      ) as Promise<HmrcVerification>,
  });
}

/**
 * Critique addendum C3: the panel keeps the returned `job_id` itself (via `useJob`) rather than this hook
 * invalidating `hmrc-verification` on success -- at `awaiting_mapping_review` the state never changes, and
 * an immediate refetch would only show the SAME "failed/allowed" shape the operator just acted on, before
 * the deferred job has even run. Invalidation happens once the job reaches a terminal status instead (the
 * panel's own `useEffect`).
 */
export function useRerunHmrc(migrationId: string) {
  return useMutation({
    mutationFn: (body: { expected_run_id: string | null }) =>
      unwrap(
        client.POST("/api/v1/migrations/{migration_id}/actions/rerun-hmrc", {
          params: { path: { migration_id: migrationId } },
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<Accepted>,
  });
}

// ---------------------------------------------------------------- review, totals, evidence, admin (08/09)
//
// (v4-BL-7) `useApproveMapping`, `usePackageManifest` and `useAuditEvents` are here because tasks 08 and 09 need
// them and neither owns `hooks.ts` (§D0). The canonical names are `useResolveReviewItem` (not `useResolveReview`),
// `useRunDelta` (not `useDelta`) and `downloadUrl` in `client.ts` (not `downloadEvidencePack`); 08 and 09 bind to
// those spellings.

export function useReviewItems(migrationId: string, query: Record<string, string | number | boolean>) {
  return useQuery({
    queryKey: keys.sub(migrationId, "review-items", JSON.stringify(query)),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/review-items", {
          params: { path: { migration_id: migrationId }, query },
        }),
      ) as Promise<Page<ReviewItem>>,
  });
}

/** The API's own hard cap (`review.py`'s `MAX_LIMIT`) -- the largest single page `list_review_items`
 * will ever return, so `fetchAllReviewItems` below needs the fewest possible round trips to exhaust it. */
const REVIEW_ITEMS_PAGE_LIMIT = 200;

async function fetchAllReviewItems(
  migrationId: string,
  query: Record<string, string | number | boolean>,
): Promise<ReviewItem[]> {
  const items: ReviewItem[] = [];
  let cursor: string | undefined;
  for (;;) {
    const page = (await unwrap(
      client.GET("/api/v1/migrations/{migration_id}/review-items", {
        params: {
          path: { migration_id: migrationId },
          query: {
            ...query,
            limit: REVIEW_ITEMS_PAGE_LIMIT,
            ...(cursor === undefined ? {} : { cursor }),
          },
        },
      }),
    )) as Page<ReviewItem>;
    items.push(...page.items);
    if (page.next_cursor === null) {
      return items;
    }
    cursor = page.next_cursor;
  }
}

/**
 * T8 round-4 review fix (findings 3/5, and their duplicate): `useReviewItems(id, {})` fetches exactly
 * ONE page -- the server's own default `limit=50`, newest id first (`list_review_items` orders by
 * `ReviewItemRow.id.desc()`). The SOURCE `journals_scope_not_granted` item is always the OLDEST review
 * row (filed at extract, before `stage_map` ever runs), so as soon as mapping files 50 or more current
 * -version items (COA renumbers, contact-duplicate candidates -- easy on a real Xero org), the SOURCE row
 * -- and any BLOCK item behind it -- is paged off entirely. The generic ("other kinds") panel then renders
 * nothing while the separate `blocking` count (fed from its own, equally single-page query) still reports
 * it as blocking: Approve stays disabled with no control anywhere on the page to resolve it -- the exact
 * round-2 web dead end this fix's own predecessor was meant to close.
 *
 * This follows `next_cursor` to exhaustion, at the API's own `MAX_LIMIT` per page, so neither `blocking`
 * nor `otherAll` in `ReviewScreen.tsx` can ever be silently truncated again, however many review items a
 * migration accumulates.
 */
export function useAllReviewItems(migrationId: string, query: Record<string, string | number | boolean>) {
  return useQuery({
    queryKey: keys.sub(migrationId, "review-items-all", JSON.stringify(query)),
    queryFn: () => fetchAllReviewItems(migrationId, query),
  });
}

export function useResolveReviewItem(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { itemId: string; body: Record<string, unknown>; mayMoveState?: boolean }) =>
      unwrap(
        client.POST("/api/v1/migrations/{migration_id}/review-items/{item_id}/resolve", {
          params: { path: { migration_id: migrationId, item_id: args.itemId } },
          // §B4.5's caller passes `{option}`; the contract types the body, so the cast is at the boundary.
          body: args.body as { option: string },
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<ReviewItem>,
    // §B4.5: the resolve writes through to the mapping row, so both queries are invalidated and the row re-renders
    // with its new target code — that is how the UI proves the write-through.
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "review-items") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "accounts") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "tax-rates") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "contacts") });
      refreshMigration(qc, migrationId, args.mayMoveState === true);
    },
  });
}

/**
 * xmg-coafast: "Accept all suggestions". One POST; the server resolves every open item that carries a
 * `suggested_option` through the same path as a single resolve (actor = the signed-in operator) and answers
 * `{resolved, skipped_needs_choice, refused}`. Invalidates exactly what a single resolve does.
 */
export function useAcceptSuggestions(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { mappingVersionId: string; itemIds?: readonly string[]; mayMoveState?: boolean }) =>
      unwrap(
        client.POST("/api/v1/migrations/{migration_id}/review-items/accept-suggestions", {
          params: { path: { migration_id: migrationId } },
          body: {
            mapping_version_id: args.mappingVersionId,
            item_ids: args.itemIds === undefined ? null : [...args.itemIds],
          },
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<AcceptSuggestionsResult>,
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "review-items") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "accounts") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "tax-rates") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "contacts") });
      refreshMigration(qc, migrationId, args.mayMoveState === true);
    },
  });
}

/**
 * FX opening-balance spec §3.7: the three FX opening-balance endpoints. All three invalidate the same
 * four query families -- the fx list itself, the review-items list (findings live there too), the scope
 * (the entered figures are frozen into it) and the migration (its `state`/polling) -- because a PUT or an
 * accept-estimate can change any of them in one call (`refresh_review_items` runs server-side on every
 * write).
 */
export function useFxOpeningBalances(migrationId: string) {
  return useQuery({
    queryKey: keys.sub(migrationId, "fx-opening-balances"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/fx-opening-balances", {
          params: { path: { migration_id: migrationId } },
        }),
      ) as Promise<FxOpeningBalances>,
  });
}

export function useSetFxOpeningBalance(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { accountId: string; body: { amount: string | null; currency: string } }) =>
      unwrap(
        client.PUT("/api/v1/migrations/{migration_id}/fx-opening-balances/{account_id}", {
          params: { path: { migration_id: migrationId, account_id: args.accountId } },
          body: args.body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<FxOpeningBalances>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "fx-opening-balances") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "review-items") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "scope") });
      refreshMigration(qc, migrationId);
    },
  });
}

/** xmg-fx-autofill: "Use our figure again" after the operator cleared a figure we had filled in. */
export function useRestoreFxOpeningAutoFigure(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { accountId: string }) =>
      unwrap(
        client.POST("/api/v1/migrations/{migration_id}/fx-opening-balances/{account_id}/use-auto-figure", {
          params: { path: { migration_id: migrationId, account_id: args.accountId } },
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<FxOpeningBalances>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "fx-opening-balances") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "review-items") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "scope") });
      refreshMigration(qc, migrationId);
    },
  });
}

export function useAcceptFxOpeningEstimate(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { accountId: string }) =>
      unwrap(
        client.POST("/api/v1/migrations/{migration_id}/fx-opening-balances/{account_id}/accept-estimate", {
          params: { path: { migration_id: migrationId, account_id: args.accountId } },
          body: {},
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<FxOpeningBalances>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "fx-opening-balances") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "review-items") });
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "scope") });
      refreshMigration(qc, migrationId);
    },
  });
}

/**
 * §B4.6 (task 08). `approve_mapping` approves the **current** mapping version without signing off. It is the
 * secondary action on the review screen: the primary path is `signoff_migration` alone, because `runner.signoff`
 * approves the current version itself and "approve then sign off" would be two non-atomic mutations doing one
 * job (§A9.5).
 */
export function useApproveMapping(migrationId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `approve_mapping` takes NO body and answers `ApproveMappingOut` - `{mapping_version: {...}}`. §B4.6's
    // caller wants the id, so the hook narrows it here rather than in every screen.
    mutationFn: async (_body: Record<string, unknown> = {}) => {
      const result = (await unwrap(
        client.POST("/api/v1/migrations/{migration_id}/mapping/approve", {
          params: { path: { migration_id: migrationId } },
          headers: idempotent(newIdempotencyKey()),
        }),
      )) as { mapping_version: { id: string } };
      return { mapping_version_id: result.mapping_version.id };
    },
    onSuccess: () => {
      refreshMigration(qc, migrationId, true);
      void qc.invalidateQueries({ queryKey: keys.sub(migrationId, "review-items") });
    },
  });
}

/** §A8.3 (task 08's sign-off dialog): the persisted manifest. `entity_hashes` is documented `null`. */
export const usePackageManifest = (id: string) =>
  useQuery({
    queryKey: keys.sub(id, "package"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/package", {
          params: { path: { migration_id: id } },
        }),
      ) as Promise<PackageManifest>,
  });

/**
 * §A8 row 22 / §A5.1 (task 09's failure panel). The newest audit row's `metadata.request_id` is §B6.7's
 * "`X-Request-Id` of the request that last touched this migration"; nothing else in the API surfaces it.
 */
export const useAuditEvents = (id: string, query: { limit?: number } = {}) =>
  useQuery({
    queryKey: keys.sub(id, "audit-events", JSON.stringify(query)),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/audit-events", {
          params: { path: { migration_id: id }, query: { limit: query.limit ?? 1 } },
        }),
      ) as Promise<Page<AuditEvent>>,
  });

export const useAccountMappings = (id: string) =>
  useQuery({
    queryKey: keys.sub(id, "accounts"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/mapping/accounts", {
          params: { path: { migration_id: id } },
        }),
      ) as Promise<{
        mapping_version_id: string | null;
        items: AccountMapping[];
        suggestions_missing?: boolean;
      }>,
  });

export const useTaxMappings = (id: string) =>
  useQuery({
    queryKey: keys.sub(id, "tax-rates"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/mapping/tax-rates", {
          params: { path: { migration_id: id } },
        }),
      ) as Promise<{ mapping_version_id: string | null; items: TaxMapping[] }>,
  });

export const useContactMerges = (id: string) =>
  useQuery({
    queryKey: keys.sub(id, "contacts"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/mapping/contacts", {
          params: { path: { migration_id: id } },
        }),
      ) as Promise<{ mapping_version_id: string | null; items: ContactMerge[] }>,
  });

export const useControlTotals = (id: string, query: Record<string, string>) =>
  useQuery({
    queryKey: keys.sub(id, "control-totals", JSON.stringify(query)),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/control-totals", {
          params: { path: { migration_id: id }, query },
        }),
      ) as Promise<{ items: ControlTotal[] }>,
  });

export const useScorecard = (id: string) =>
  useQuery({
    queryKey: keys.sub(id, "scorecard"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/scorecard", {
          params: { path: { migration_id: id } },
        }),
      ) as Promise<Scorecard>,
  });

export const useEvidencePacks = (id: string) =>
  useQuery({
    queryKey: keys.sub(id, "evidence"),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/migrations/{migration_id}/evidence", {
          params: { path: { migration_id: id } },
        }),
      ) as Promise<{ items: EvidencePack[] }>,
  });

export const useConnectorSwitches = () =>
  useQuery({
    queryKey: keys.admin("connector-switches"),
    queryFn: () =>
      unwrap(client.GET("/api/v1/admin/connector-switches", {})) as Promise<{ items: ConnectorSwitch[] }>,
  });

export function useSetConnectorSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      system: string;
      entity: string;
      direction: string;
      body: { enabled: boolean; reason: string };
    }) =>
      unwrap(
        client.PUT("/api/v1/admin/connector-switches/{system}/{entity}/{direction}", {
          // The contract types `entity` as the canonical `Entity` enum and `direction` as
          // `"source" | "destination"`; the screen passes plain strings, so the cast is at the boundary.
          params: {
            path: {
              system: args.system,
              entity: args.entity as Entity,
              direction: args.direction as "source" | "destination",
            },
          },
          body: args.body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<ConnectorSwitch>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.admin("connector-switches") }),
  });
}

export const useVendorAlerts = () =>
  useQuery({
    queryKey: keys.admin("vendor-alerts"),
    queryFn: () => unwrap(client.GET("/api/v1/admin/vendor-alerts", {})) as Promise<{ items: VendorAlert[] }>,
  });

export const useVendorLimits = () =>
  useQuery({
    queryKey: keys.admin("vendor-limits"),
    queryFn: () => unwrap(client.GET("/api/v1/admin/vendor-limits", {})) as Promise<{ items: VendorLimit[] }>,
  });

export function usePurge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { dry_run: boolean }) =>
      unwrap(
        client.POST("/api/v1/admin/purge", { body, headers: idempotent(newIdempotencyKey()) }),
        // Endpoint 54 answers `JobAcceptedOut`; `counts` is optional because §B6.4's dry-run table renders
        // it only when the API supplies one, and today the sweep is deferred and supplies none.
      ) as Promise<Accepted & { counts?: PurgeCount[] }>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["migrations"] }),
  });
}

// ---------------------------------------------------------------- P5-web: practice mode
// One hook per operation, same convention as the rest of this file. `sync_practice_clients` currently answers
// `503 upstream_unavailable` in a real deployment (the LuciBook reader is not wired) — the hook does not swallow
// that, and every caller renders the resulting `ApiError` as an informational banner rather than a failure.

export function usePractices(): UseQueryResult<Page<PracticeSummary>> {
  return useQuery({
    queryKey: keys.practices,
    queryFn: () => unwrap(client.GET("/api/v1/practices", {})) as Promise<Page<PracticeSummary>>,
  });
}

export function useCreatePractice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePracticeBody) =>
      unwrap(
        client.POST("/api/v1/practices", { body, headers: idempotent(newIdempotencyKey()) }),
      ) as Promise<PracticeDetail>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.practices }),
  });
}

export function usePractice(practiceId: string | null): UseQueryResult<PracticeDetail> {
  return useQuery({
    queryKey: keys.practice(practiceId ?? ""),
    enabled: practiceId !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/practices/{practice_id}", {
          params: { path: { practice_id: practiceId as string } },
        }),
      ) as Promise<PracticeDetail>,
  });
}

export function usePracticeMembers(practiceId: string): UseQueryResult<Page<Member>> {
  return useQuery({
    queryKey: keys.practiceMembers(practiceId),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/practices/{practice_id}/members", {
          params: { path: { practice_id: practiceId } },
        }),
      ) as Promise<Page<Member>>,
  });
}

export function useChangeMemberRole(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: string; body: ChangeRoleBody }) =>
      withStepUp(
        () =>
          unwrap(
            client.POST("/api/v1/practices/{practice_id}/members/{user_id}/role", {
              params: { path: { practice_id: practiceId, user_id: args.userId } },
              body: args.body,
              headers: idempotent(newIdempotencyKey()),
            }),
          ) as Promise<Member>,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.practiceMembers(practiceId) }),
  });
}

export function useRemoveMember(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      withStepUp(() =>
        unwrap(
          client.DELETE("/api/v1/practices/{practice_id}/members/{user_id}", {
            params: { path: { practice_id: practiceId, user_id: userId } },
          }),
        ),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.practiceMembers(practiceId) }),
  });
}

export function usePracticeInvitations(practiceId: string): UseQueryResult<Page<Invitation>> {
  return useQuery({
    queryKey: keys.practiceInvitations(practiceId),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/practices/{practice_id}/invitations", {
          params: { path: { practice_id: practiceId } },
        }),
      ) as Promise<Page<Invitation>>,
  });
}

export function useCreateInvitation(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInvitationBody) =>
      withStepUp(
        () =>
          unwrap(
            client.POST("/api/v1/practices/{practice_id}/invitations", {
              params: { path: { practice_id: practiceId } },
              body,
              headers: idempotent(newIdempotencyKey()),
            }),
          ) as Promise<Invitation>,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.practiceInvitations(practiceId) }),
  });
}

export function useRevokeInvitation(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    // xmg-sec-mfafollow: revoking an invitation now needs a recent second factor, like creating one.
    mutationFn: (invitationId: string) =>
      withStepUp(() =>
        unwrap(
          client.DELETE("/api/v1/practices/{practice_id}/invitations/{invitation_id}", {
            params: { path: { practice_id: practiceId, invitation_id: invitationId } },
          }),
        ),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.practiceInvitations(practiceId) }),
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (args: { token: string; body: AcceptInvitationBody }) =>
      unwrap(
        client.POST("/api/v1/invitations/{token}/accept", {
          params: { path: { token: args.token } },
          body: args.body,
        }),
      ) as Promise<AcceptInvitationResult>,
  });
}

export function usePracticeClients(
  practiceId: string,
  cursor: string | null,
): UseQueryResult<Page<PracticeClient>> {
  return useQuery({
    queryKey: keys.practiceClients(practiceId, cursor),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/practices/{practice_id}/clients", {
          params: { path: { practice_id: practiceId }, query: cursor === null ? {} : { cursor } },
        }),
      ) as Promise<Page<PracticeClient>>,
    placeholderData: (previous) => previous,
  });
}

/**
 * Fable #14: `BatchRunsTab` used to read its list of batch runs out of `sessionStorage`
 * (`screens/practice/batchRunSession.ts`) because `GET /practices/{practice_id}/batch-runs` did not exist
 * yet. It landed (P5-backend-2), so this hook is the durable, cross-device, cross-tab replacement -- the
 * same convention as every other paged practice list in this file.
 */
export function useBatchRuns(
  practiceId: string,
  cursor: string | null = null,
): UseQueryResult<Page<BatchRunSummary>> {
  return useQuery({
    queryKey: keys.practiceBatchRuns(practiceId, cursor),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/practices/{practice_id}/batch-runs", {
          params: { path: { practice_id: practiceId }, query: cursor === null ? {} : { cursor } },
        }),
      ) as Promise<Page<BatchRunSummary>>,
    // §A9.8-style reasonable poll for a detail-adjacent list: batch runs move on their own (approvals,
    // starts), and this is no longer a browser-local snapshot that only changes when THIS tab writes it.
    refetchInterval: 10_000,
    placeholderData: (previous) => previous,
  });
}

export function useSyncPracticeClients(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        client.POST("/api/v1/practices/{practice_id}/clients/sync", {
          params: { path: { practice_id: practiceId } },
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<Accepted>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.practiceClients(practiceId, null) }),
  });
}

export function useCreateBatchRun(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBatchRunBody) =>
      unwrap(
        client.POST("/api/v1/practices/{practice_id}/batch-runs", {
          params: { path: { practice_id: practiceId } },
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<BatchRunCreated>,
    // Fable #14: the practice's batch-run list is now a real query (`useBatchRuns`), not a sessionStorage
    // read that updated itself the moment `ClientsTab` wrote to it -- invalidating here is what makes a
    // freshly created run show up on `BatchRunsTab`/the overview count without waiting for their poll.
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.practiceBatchRuns(practiceId, null) }),
  });
}

export function useBatchRun(batchRunId: string | null): UseQueryResult<BatchRunDetail> {
  return useQuery({
    queryKey: keys.batchRun(batchRunId ?? ""),
    enabled: batchRunId !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/batch-runs/{batch_run_id}", {
          params: { path: { batch_run_id: batchRunId as string } },
        }),
      ) as Promise<BatchRunDetail>,
  });
}

export function useBatchRunClients(
  batchRunId: string,
  cursor: string | null,
): UseQueryResult<Page<ClientMigrationState>> {
  return useQuery({
    queryKey: keys.batchRunClients(batchRunId, cursor),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/batch-runs/{batch_run_id}/clients", {
          params: { path: { batch_run_id: batchRunId }, query: cursor === null ? {} : { cursor } },
        }),
      ) as Promise<Page<ClientMigrationState>>,
    placeholderData: (previous) => previous,
  });
}

export function useSendBatchRunApprovals(batchRunId: string) {
  return useMutation({
    mutationFn: (body: { client_ids: string[]; scopes: string[] }) =>
      unwrap(
        client.POST("/api/v1/batch-runs/{batch_run_id}/approvals", {
          params: { path: { batch_run_id: batchRunId } },
          body,
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<Accepted>,
  });
}

export function useStartBatchRun(batchRunId: string) {
  return useMutation({
    mutationFn: () =>
      unwrap(
        client.POST("/api/v1/batch-runs/{batch_run_id}/start", {
          params: { path: { batch_run_id: batchRunId } },
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<Accepted>,
  });
}

export function useCancelBatchRun(batchRunId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        client.POST("/api/v1/batch-runs/{batch_run_id}/cancel", {
          params: { path: { batch_run_id: batchRunId } },
          headers: idempotent(newIdempotencyKey()),
        }),
      ) as Promise<BatchRunDetail>,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.batchRun(batchRunId) }),
  });
}

export function useBatchRunCompleteness(
  batchRunId: string | null,
  format?: "csv",
): UseQueryResult<BatchCompleteness> {
  return useQuery({
    queryKey: keys.batchRunCompleteness(batchRunId ?? ""),
    enabled: batchRunId !== null && format === undefined,
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/batch-runs/{batch_run_id}/completeness", {
          params: { path: { batch_run_id: batchRunId as string } },
        }),
      ) as Promise<BatchCompleteness>,
  });
}

export function useAuthorityGaps(practiceId: string): UseQueryResult<Page<AuthorityGap>> {
  return useQuery({
    queryKey: keys.authorityGaps(practiceId),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/practices/{practice_id}/authority-gaps", {
          params: { path: { practice_id: practiceId } },
        }),
      ) as Promise<Page<AuthorityGap>>,
  });
}

export function useRateBudget(practiceId: string): UseQueryResult<RateBudget> {
  return useQuery({
    queryKey: keys.rateBudget(practiceId),
    queryFn: () =>
      unwrap(
        client.GET("/api/v1/practices/{practice_id}/rate-budget", {
          params: { path: { practice_id: practiceId } },
        }),
      ) as Promise<RateBudget>,
  });
}
